import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { Spinner } from "@/shared/ui/spinner";

import { canRequestClip, isClipWorking } from "../../model/clipState";
import type { ClipError, HighlightView } from "../../model/Highlight";

// Ферма копит заказы до 15 минут (`farm_batch_minutes`), просыпается ~3 мин, запись
// матча — ещё пара минут.
const QUEUED = msg`In queue · up to 30 min`;
const RENDERING = msg`Recording the clip`;
const MAKE = msg`Make a clip`;
const RETRY = msg`Try again`;
const ERRORS: Record<ClipError, ReturnType<typeof msg>> = {
  demo_outdated: msg`The game has updated: this match's demo no longer opens`,
  demo_unavailable: msg`Valve no longer has this match's demo`,
  player_not_found: msg`Could not find you in the replay`,
  render_failed: msg`Recording failed`,
};

type ClipActionProps = {
  highlight: HighlightView;
  // Запись клипов на сервере настроена; нет — кнопки нет.
  clipsEnabled: boolean;
  requesting: boolean;
  onRequest: () => void;
  className?: string;
};

// Что можно сделать с моментом, у которого нет готового клипа: «Сделать клип», статус
// записи со спиннером или почему не вышло (сбой записи — «Повторить»).
export const ClipAction = ({
  highlight,
  clipsEnabled,
  requesting,
  onRequest,
  className,
}: ClipActionProps) => {
  const { i18n } = useLingui();
  const { clip } = highlight;
  if (isClipWorking(highlight)) {
    return (
      <span
        role="status"
        className={cn("flex min-h-10 items-center gap-2 text-sm text-foreground-soft", className)}
      >
        <Spinner aria-hidden="true" className="size-4 text-accent" />
        {i18n._(clip.status === "queued" ? QUEUED : RENDERING)}
      </span>
    );
  }
  const failed = clip.status === "failed" && clip.error !== null;
  const button = clipsEnabled && canRequestClip(highlight) && (
    <Button variant="outline" loading={requesting} onClick={onRequest}>
      {i18n._(failed ? RETRY : MAKE)}
    </Button>
  );
  if (!failed && !button) {
    return null;
  }
  return (
    <span className={cn("flex flex-wrap items-center gap-2", className)}>
      {failed && clip.error !== null && (
        <span className="text-sm text-bad">{i18n._(ERRORS[clip.error])}</span>
      )}
      {button}
    </span>
  );
};
